import React, { useEffect, useState } from 'react';
import {
  Box, TextField, Button, Switch, FormControlLabel,
  Typography, Alert, Stack, Grid, MenuItem, CircularProgress, Paper, Chip,
} from '@mui/material';
import api from '../services/api';

/**
 * FedEx integration settings (v10.18.4+).
 *
 * Sandbox and production credentials are stored in separate slots so
 * the operator can have both filled in at once and the Environment
 * toggle just picks which set is active. Pre-v10.18.4 there was one
 * credential trio shared between both URLs, which meant flipping the
 * toggle would either send sandbox creds to the production URL (and
 * fail) or clobber the previously-stored sandbox creds with whatever
 * the operator typed for production.
 *
 * client_secret round-trips as bullets •••• with the last 4 chars
 * visible — once per env. Leave the secret field blank on save to
 * keep that env's existing secret.
 */
const EMPTY = { client_id: '', client_secret: '', account_number: '' };

export default function FedExSettings() {
  const [cfg, setCfg] = useState({
    enabled: false,
    environment: 'sandbox',
    sandbox: { ...EMPTY },
    production: { ...EMPTY },
    shipper: {
      name: '', phone: '',
      street1: '', street2: '',
      city: '', state_or_province_code: '',
      postal_code: '', country_code: 'US',
    },
  });
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get('/api/fedex/config');
        if (cancelled) return;
        setCfg((prev) => ({
          ...prev, ...data,
          sandbox: { ...prev.sandbox, ...(data.sandbox || {}) },
          production: { ...prev.production, ...(data.production || {}) },
          shipper: { ...prev.shipper, ...(data.shipper || {}) },
        }));
      } catch (_e) { /* ok — config not set yet */ }
      finally { if (!cancelled) setLoaded(true); }
    })();
    return () => { cancelled = true; };
  }, []);

  const set = (k, v) => setCfg((p) => ({ ...p, [k]: v }));
  const setS = (k, v) => setCfg((p) => ({ ...p, shipper: { ...p.shipper, [k]: v } }));
  const setEnvCred = (env, k, v) =>
    setCfg((p) => ({ ...p, [env]: { ...p[env], [k]: v } }));

  // Strip masked bullets out of either env's client_secret before
  // sending — the backend keeps the existing secret when blank.
  const stripMaskedSecret = (creds) => {
    const out = { ...creds };
    if ((out.client_secret || '').includes('•')) {
      delete out.client_secret;
    }
    return out;
  };

  const save = async () => {
    setSaving(true);
    setResult(null);
    try {
      const payload = {
        ...cfg,
        sandbox: stripMaskedSecret(cfg.sandbox),
        production: stripMaskedSecret(cfg.production),
      };
      await api.put('/api/fedex/config', payload);
      setResult({ severity: 'success', msg: 'Saved.' });
    } catch (e) {
      setResult({ severity: 'error', msg: e.response?.data?.detail || e.message });
    } finally { setSaving(false); }
  };

  const test = async () => {
    setTesting(true);
    setResult(null);
    try {
      const { data } = await api.post('/api/fedex/test-connection');
      // v10.18.5: probe surfaces OAuth + Ship API authorization
      // separately. OAuth passing doesn't mean Schedule FedEx will
      // work — production credentials often pass OAuth but fail
      // Ship until the project is approved for prod on
      // developer.fedex.com. Show both signals explicitly.
      if (data.ship_authorized) {
        setResult({
          severity: 'success',
          msg: `Ready — ${data.environment} · acct ${data.account_number}. OAuth + Ship API both authorized.`,
        });
      } else {
        setResult({
          severity: 'warning',
          msg: `OAuth works but Ship API is NOT authorized: ${data.ship_error || 'no detail returned'}`,
        });
      }
    } catch (e) {
      setResult({ severity: 'error', msg: e.response?.data?.detail || e.message });
    } finally { setTesting(false); }
  };

  if (!loaded) return <CircularProgress size={24} />;

  const credSectionFor = (env, label, accent) => (
    <Paper variant="outlined" sx={{ p: 2, borderColor: accent }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
        <Typography variant="subtitle1" fontWeight={600}>{label}</Typography>
        {cfg.environment === env && (
          <Chip size="small" color="primary" label="Active" sx={{ height: 18, fontSize: '0.65rem' }} />
        )}
      </Stack>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <TextField fullWidth size="small" label="Client ID (API Key)"
                     value={cfg[env].client_id}
                     onChange={(e) => setEnvCred(env, 'client_id', e.target.value)} />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField fullWidth size="small" label="Client Secret (Secret Key)"
                     value={cfg[env].client_secret}
                     onChange={(e) => setEnvCred(env, 'client_secret', e.target.value)}
                     helperText="Leave blank to keep existing secret" />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField fullWidth size="small" label="Account number"
                     value={cfg[env].account_number}
                     onChange={(e) => setEnvCred(env, 'account_number', e.target.value)}
                     helperText={env === 'sandbox' ? 'FedEx test account: 740561073' : 'HHMG production FedEx account number'} />
        </Grid>
      </Grid>
    </Paper>
  );

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 1 }}>FedEx integration</Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
        Credentials for the FedEx Ship + Track Web Services APIs. Sandbox and
        production each have their own slot; the active environment selects
        which set is used by Schedule FedEx / Track / Test Connection.
      </Typography>
      <Alert severity="info" sx={{ mb: 2 }}>
        <Typography variant="caption" sx={{ display: 'block' }}>
          <strong>Production Ship API access is gated by FedEx&apos;s Shipper
          Validation workflow,</strong> not a self-serve toggle. After moving
          the project to production, you must (1) generate test labels in
          sandbox, (2) email them with a Label Cover Sheet to{' '}
          <code>label@fedex.com</code> referencing the production account +
          key, (3) FedEx&apos;s Bar Code Analysis Group reviews (~3 business
          days). Until validation completes, Schedule FedEx on production
          will return 403. Use the Sandbox environment for end-to-end
          testing in the meantime.
        </Typography>
      </Alert>

      <Stack spacing={2}>
        <Stack direction="row" spacing={2} alignItems="center">
          <FormControlLabel
            control={<Switch checked={cfg.enabled} onChange={(e) => set('enabled', e.target.checked)} />}
            label="Enable FedEx integration"
          />
          <TextField select size="small" label="Active environment" value={cfg.environment}
                     onChange={(e) => set('environment', e.target.value)} sx={{ minWidth: 220 }}>
            <MenuItem value="sandbox">Sandbox (test)</MenuItem>
            <MenuItem value="production">Production</MenuItem>
          </TextField>
        </Stack>

        {credSectionFor('sandbox', 'Sandbox credentials', '#1976d2')}
        {credSectionFor('production', 'Production credentials', '#9c27b0')}

        <Typography variant="subtitle2" sx={{ mt: 2 }}>Shipper origin (pickup address)</Typography>
        <Typography variant="caption" color="text.secondary">
          Same address for both environments.
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField fullWidth size="small" label="Company / contact name"
                       value={cfg.shipper.name} onChange={(e) => setS('name', e.target.value)} />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth size="small" label="Phone"
                       value={cfg.shipper.phone} onChange={(e) => setS('phone', e.target.value)}
                       placeholder="3105551234" />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth size="small" label="Street line 1"
                       value={cfg.shipper.street1} onChange={(e) => setS('street1', e.target.value)} />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth size="small" label="Street line 2 (optional)"
                       value={cfg.shipper.street2} onChange={(e) => setS('street2', e.target.value)} />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField fullWidth size="small" label="City"
                       value={cfg.shipper.city} onChange={(e) => setS('city', e.target.value)} />
          </Grid>
          <Grid item xs={6} md={2}>
            <TextField fullWidth size="small" label="State"
                       value={cfg.shipper.state_or_province_code}
                       onChange={(e) => setS('state_or_province_code', e.target.value)} />
          </Grid>
          <Grid item xs={6} md={3}>
            <TextField fullWidth size="small" label="Postal code"
                       value={cfg.shipper.postal_code}
                       onChange={(e) => setS('postal_code', e.target.value)} />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField fullWidth size="small" label="Country"
                       value={cfg.shipper.country_code}
                       onChange={(e) => setS('country_code', e.target.value)} />
          </Grid>
        </Grid>

        {result && <Alert severity={result.severity}>{result.msg}</Alert>}

        <Stack direction="row" spacing={1}>
          <Button variant="contained" onClick={save} disabled={saving}>
            {saving ? <CircularProgress size={18} /> : 'Save'}
          </Button>
          <Button variant="outlined" onClick={test} disabled={testing || !cfg.enabled}>
            {testing ? <CircularProgress size={18} /> : `Test ${cfg.environment} connection`}
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
}
